import { createSelector } from "@reduxjs/toolkit";

import { organizationSelector } from "Reducers/organization/organization";
// Import from the specific selectors file (not the barrel) to avoid pulling
// the projects slice/listeners into the reducer-init graph, which creates a
// circular-import ordering issue.
import { organizationProjectSelector } from "Reducers/organization/projects/projects.selectors";

import type { RootState } from "Store/configureStore";

export const addonsStateSelector = (state: RootState) => state.addons;

export const loadingAddonsSelector = createSelector(
  addonsStateSelector,
  addonsState => addonsState.loadingAddons
);

export const addonsDataSelector = createSelector(
  addonsStateSelector,
  addonsState => addonsState.data
);

export const addonsSelector = createSelector(
  addonsDataSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (data, { organizationId }) => data?.[organizationId]
);

export const addonsCurrentSelector = createSelector(
  addonsSelector,
  addons => addons?.current
);

export const addonsAvailableSelector = createSelector(
  addonsSelector,
  addons => addons?.available
);

export const addonsDetailsSelector = createSelector(
  addonsSelector,
  addons => addons?.details
);

// Price of the standard user management add-on as returned by the `/addons`
// endpoint. Used instead of the organization estimate for organizations on the
// new billing system (`billing_legacy === false`).
export const userManagementStandardPriceSelector = createSelector(
  addonsDetailsSelector,
  details => details?.user_management?.standard?.price
);

export const addonsErrorSelector = createSelector(
  addonsStateSelector,
  addonsState => addonsState?.error
);

export const userManagementAddonCurrentSelector = createSelector(
  addonsCurrentSelector,
  current => current?.user_management
);

export const currentSupportLevelSelector = createSelector(
  [
    organizationSelector,
    addonsDataSelector,
    (
      state: RootState,
      params: { organizationId?: string; projectId?: string }
    ) => {
      const organization = params.organizationId
        ? organizationSelector(state, { organizationId: params.organizationId })
        : undefined;
      if (!organization || !params.projectId) return undefined;
      return organizationProjectSelector(state, {
        organizationId: organization.name,
        projectId: params.projectId
      });
    }
  ],
  (organization, addonsData, orgProject) => {
    if (organization?.type === "fixed") {
      return orgProject?.support_tier ?? null;
    }
    if (!organization) return null;
    const addons = addonsData?.[organization.name];
    return addons?.current?.support_level
      ? Object.keys(addons.current.support_level)[0]
      : null;
  }
);
